import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { MessageCircle, X, Send, Sparkles } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORAGE_KEY = "chat_session_id_v1";

/**
 * Floating chat assistant "Théo" powered by Claude Sonnet 4.5.
 * Bottom-right pill. Opens a panel with multi-turn conversation persisted per session.
 */
export default function ChatAssistant() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [sessionId, setSessionId] = useState(() => {
    try { return localStorage.getItem(STORAGE_KEY) || null; } catch { return null; }
  });
  const scrollRef = useRef(null);

  // Load history on open
  useEffect(() => {
    if (!open || !sessionId) return;
    axios.get(`${API}/chat/history`, { params: { session_id: sessionId } }).then((r) => {
      setMessages(r.data?.items || []);
    }).catch(() => {});
  }, [open, sessionId]);

  // Auto-scroll
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, open]);

  async function send(e) {
    e?.preventDefault();
    const text = input.trim();
    if (!text || sending) return;
    setSending(true);
    const userMsg = { role: "user", text, id: `tmp-${Date.now()}` };
    setMessages((m) => [...m, userMsg]);
    setInput("");
    try {
      const { data } = await axios.post(`${API}/chat/message`, {
        session_id: sessionId,
        message: text,
      });
      if (data.session_id && data.session_id !== sessionId) {
        setSessionId(data.session_id);
        try { localStorage.setItem(STORAGE_KEY, data.session_id); } catch { /* ignore */ }
      }
      setMessages((m) => [...m, { role: "assistant", text: data.reply, id: `res-${Date.now()}` }]);
    } catch (err) {
      setMessages((m) => [...m, { role: "assistant", text: "Désolé, je rencontre un souci technique. Réessayez dans un instant.", id: `err-${Date.now()}`, error: true }]);
    } finally {
      setSending(false);
    }
  }

  const isEmpty = messages.length === 0;

  return (
    <>
      {/* Floating button */}
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-label="Ouvrir l'assistant"
        data-testid="chat-open-btn"
        className={`fixed z-[70] bottom-24 md:bottom-16 right-5 rounded-full shadow-xl transition-all ${open ? "bg-ink text-white w-12 h-12" : "bg-coral text-white px-5 py-3.5 hover:scale-105"}`}
      >
        {open ? <X size={20} className="mx-auto" /> : (
          <span className="inline-flex items-center gap-2 text-sm font-medium">
            <MessageCircle size={18} /> Poser une question
          </span>
        )}
      </button>

      {/* Chat panel */}
      {open && (
        <div
          data-testid="chat-panel"
          className="fixed z-[69] bottom-32 right-5 w-[calc(100vw-2.5rem)] max-w-md h-[560px] max-h-[75vh] bg-white rounded-3xl border border-ink/10 shadow-2xl flex flex-col overflow-hidden"
        >
          <header className="px-5 py-4 border-b border-ink/10 bg-gradient-to-br from-pedablue to-pedablue/90 text-ivory">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-white/15 flex items-center justify-center">
                <Sparkles size={18} />
              </div>
              <div>
                <p className="font-display text-lg">Théo</p>
                <p className="text-[10px] uppercase tracking-widest opacity-70">Assistant IA · orthophonie</p>
              </div>
            </div>
          </header>

          <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3 bg-ivory-100">
            {isEmpty && (
              <div className="text-center pt-6" data-testid="chat-empty">
                <p className="font-display text-lg mb-2">Bonjour !</p>
                <p className="text-sm text-ink-soft">Je suis Théo, votre assistant orthophonie. Posez-moi une question sur les troubles DYS, TSA, TDAH ou le choix d&apos;un produit.</p>
                <div className="mt-4 space-y-2">
                  {[
                    "Mon enfant de 4 ans ne parle pas encore",
                    "Comment aider un enfant dyslexique ?",
                    "Quel jeu pour la phonologie ?",
                  ].map((sug) => (
                    <button
                      key={sug}
                      type="button"
                      onClick={() => setInput(sug)}
                      className="block w-full text-start text-xs px-3 py-2 rounded-full bg-white border border-ink/10 hover:bg-pedablue/5"
                      data-testid={`chat-suggestion-${sug.slice(0, 15)}`}
                    >
                      {sug}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {messages.map((m) => (
              <div
                key={m.id}
                className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
                data-testid={`chat-msg-${m.role}`}
              >
                <div className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm ${
                  m.role === "user"
                    ? "bg-pedablue text-ivory rounded-br-sm"
                    : m.error
                      ? "bg-coral/10 text-coral-dark rounded-bl-sm"
                      : "bg-white border border-ink/10 rounded-bl-sm"
                }`}>
                  <p className="whitespace-pre-wrap leading-relaxed">{m.text}</p>
                </div>
              </div>
            ))}
            {sending && (
              <div className="flex justify-start" data-testid="chat-typing">
                <div className="bg-white border border-ink/10 rounded-2xl rounded-bl-sm px-4 py-3">
                  <div className="flex gap-1">
                    <span className="w-2 h-2 rounded-full bg-ink/30 animate-bounce" style={{animationDelay:"0ms"}}></span>
                    <span className="w-2 h-2 rounded-full bg-ink/30 animate-bounce" style={{animationDelay:"150ms"}}></span>
                    <span className="w-2 h-2 rounded-full bg-ink/30 animate-bounce" style={{animationDelay:"300ms"}}></span>
                  </div>
                </div>
              </div>
            )}
          </div>

          <form onSubmit={send} className="p-3 border-t border-ink/10 bg-white">
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Votre message…"
                maxLength={2000}
                data-testid="chat-input"
                className="flex-1 bg-ivory-200 rounded-full px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-pedablue/30"
              />
              <button type="submit" disabled={!input.trim() || sending} className="w-10 h-10 rounded-full bg-ink text-white disabled:opacity-40 flex items-center justify-center" data-testid="chat-send-btn">
                <Send size={16} />
              </button>
            </div>
            <p className="text-[10px] text-ink-soft mt-1 text-center italic">Théo est une IA. Ses réponses ne remplacent pas un diagnostic médical.</p>
          </form>
        </div>
      )}
    </>
  );
}
